import { FC, useState } from "react";
import UserActionBlock from "./UserActionBlock";
import UserActionModal from "@/components/Modals/UserActionModal";
import { User } from "@/types/Users";
import LoggingAPI from "@/api/logging";
import { usePermissions } from "@/hooks/usePermissions";
import { useSnackbar } from "notistack";
import { userApi } from "@/api";

interface UserActivationBlockProps {
  userData: User;
  onSubmit?: () => void;
}

const UserActivationBlock: FC<UserActivationBlockProps> = ({
  userData,
  onSubmit,
}) => {
  const [showActivateUserModal, setShowActivateUserModal] = useState(false);

  const { currentUser } = usePermissions();
  const { enqueueSnackbar } = useSnackbar();

  const handleSubmit = async () => {
    if (!userData?.email) {
      enqueueSnackbar(
        "An error occurred resetting user password: invalid email.",
        {
          variant: "error",
        }
      );
      console.log("An error occurred resetting user password: invalid email.");
      return;
    }

    await userApi.updateUser(userData.user_id, { active: !userData.active });
    enqueueSnackbar(`User ${!userData.active ? "Activated" : "Deactivated"}`, {
      variant: !userData.active ? "success" : "error",
    });

    LoggingAPI.createLog({
      user_id: currentUser.user_id,
      message: `User ${!userData.active ? "activated" : "deactivated"}: ${
        userData?.email
      }`,
      event: "user:activate",
      object_type: "user",
    });

    onSubmit && onSubmit();
  };

  return (
    <>
      <UserActionBlock
        title="User Activation"
        tooltip="Toggle users active status."
        buttonProps={{
          primaryText: `${userData.active ? "Deactivate" : "Activate"} User`,
          color: userData.active ? "error" : "green",
          action: () => setShowActivateUserModal(true),
        }}
      />
      <UserActionModal
        show={showActivateUserModal}
        onCancel={() => setShowActivateUserModal(false)}
        onClose={() => setShowActivateUserModal(false)}
        onSubmit={handleSubmit}
        title={`${userData.active ? "Deactivate" : "Activate"} User`}
        body={`Would you like to ${
          userData.active ? "deactivate" : "activate"
        } the user?`}
        buttonProps={{
          submitText: `${userData.active ? "Deactivate" : "Activate"} User`,
          submitColor: userData.active ? "error" : "green",
        }}
      />
    </>
  );
};

export default UserActivationBlock;
